import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePageSEO } from '../hooks/usePageSEO';
import { buildBlogPostSchema, buildBlogIndexSchema } from '../utils/blogSchema';
import { MarketingHeader, MarketingFooter } from './OtherPages';
import RocketLoader from '../components/RocketLoader';
import {
  ArrowRight, ArrowLeft, ChevronDown, ChevronRight, Mail, Globe, Linkedin, Clock, Eye, Tag,
  Calendar, Users, ShieldCheck, Search, X, TrendingUp, Target, Mic, Briefcase, Sparkles,
  Layers, FileText, CheckCircle2, DollarSign, Rocket,
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '/api';

/* ═══════════════════════════
   DATA
═══════════════════════════ */
const TOOLKIT_ITEMS = [
  { label: 'Resume Builder', path: '/build/resume' },
  { label: 'ATS Checker', path: '/ats-checker' },
  { label: 'AI Interview', path: '/ai-video-interview' },
  { label: 'Cover Letter', path: '/build/cover_letter' },
  { label: 'LinkedIn Optimizer', path: '/signup' },
  { label: 'Job Tracker', path: '/signup' },
];

const SEARCH_FILTERS = [
  { label: 'Resume', kw: ['resume', 'cv'] },
  { label: 'ATS', kw: ['ats', 'applicant tracking', 'keyword'] },
  { label: 'Interview', kw: ['interview', 'hirevue'] },
  { label: 'Career', kw: ['career', 'promotion', 'growth', 'mistake'] },
  { label: 'LinkedIn', kw: ['linkedin'] },
  { label: 'AI', kw: ['ai', 'chatgpt', 'tech'] },
  { label: 'Job Search', kw: ['job search', 'job market', 'hiring', 'recruitment'] },
  { label: 'Cover Letter', kw: ['cover letter'] },
];

const POPULAR_TOPICS = [
  { label: 'Resume Writing', Icon: FileText, kw: ['resume', 'cv'], color: '#EF1D28', bg: '#FFF1F2' },
  { label: 'ATS Optimization', Icon: Target, kw: ['ats', 'applicant tracking', 'keyword'], color: '#7C3AED', bg: '#F5F3FF' },
  { label: 'Interview Tips', Icon: Mic, kw: ['interview'], color: '#2563EB', bg: '#EFF6FF' },
  { label: 'Career Growth', Icon: TrendingUp, kw: ['career', 'growth', 'promotion'], color: '#059669', bg: '#ECFDF5' },
  { label: 'LinkedIn', Icon: Linkedin, kw: ['linkedin'], color: '#0A66C2', bg: '#E8F0FE' },
  { label: 'Remote Jobs', Icon: Globe, kw: ['remote', 'work from home'], color: '#D97706', bg: '#FFFBEB' },
  { label: 'Salary', Icon: DollarSign, kw: ['salary', 'negotiation', 'pay'], color: '#DB2777', bg: '#FDF2F8' },
  { label: 'AI Tools', Icon: Sparkles, kw: ['ai', 'chatgpt'], color: '#4F46E5', bg: '#EEF2FF' },
];

const FREE_TOOLS = [
  { title: 'AI Resume Builder', desc: 'Build a complete, ATS-ready resume in minutes with AI.', Icon: Sparkles, color: '#EF1D28', bg: '#FFF1F2', path: '/build/resume' },
  { title: 'ATS Resume Checker', desc: 'Instantly score your resume against real ATS software.', Icon: Target, color: '#7C3AED', bg: '#F5F3FF', path: '/ats-checker' },
  { title: 'Interview Practice', desc: 'Practice with an AI interviewer and get real-time feedback.', Icon: Mic, color: '#2563EB', bg: '#EFF6FF', path: '/ai-video-interview' },
  { title: 'Cover Letter Generator', desc: 'Generate a tailored cover letter that matches your resume.', Icon: Mail, color: '#059669', bg: '#ECFDF5', path: '/build/cover_letter' },
  { title: 'LinkedIn Optimizer', desc: 'Get an AI-scored audit of your LinkedIn profile.', Icon: Linkedin, color: '#0A66C2', bg: '#E8F0FE', path: '/signup' },
  { title: 'Resume Templates', desc: 'Browse 40+ ATS-friendly templates for every career.', Icon: Layers, color: '#D97706', bg: '#FFFBEB', path: '/templates' },
];

const LEARNING_PATH = [
  { n: 1, title: 'Resume Builder', desc: 'Create your base resume with AI in minutes.', Icon: FileText, path: '/build/resume' },
  { n: 2, title: 'ATS Check', desc: 'Score it against real applicant tracking software.', Icon: Target, path: '/ats-checker' },
  { n: 3, title: 'Job Tailoring', desc: 'Match your resume to each job description.', Icon: Briefcase, path: '/signup' },
  { n: 4, title: 'Cover Letter', desc: 'Generate a matching letter in one click.', Icon: Mail, path: '/build/cover_letter' },
  { n: 5, title: 'Interview Practice', desc: 'Rehearse with an AI interviewer on camera.', Icon: Mic, path: '/ai-video-interview' },
  { n: 6, title: 'Apply With Confidence', desc: 'Track applications and land the offer.', Icon: Rocket, path: '/signup' },
];

const ARTICLE_TAGS = [
  { label: 'ATS', kw: ['ats'] },
  { label: 'Resume', kw: ['resume', 'cv'] },
  { label: 'Interview', kw: ['interview'] },
  { label: 'LinkedIn', kw: ['linkedin'] },
  { label: 'Remote Work', kw: ['remote', 'work from home'] },
  { label: 'Career', kw: ['career'] },
  { label: 'AI', kw: ['ai', 'chatgpt'] },
  { label: 'Salary', kw: ['salary', 'negotiation'] },
  { label: 'Hiring', kw: ['hiring', 'recruitment', 'hiring trends'] },
  { label: 'Technology', kw: ['tech', 'ai', 'chatgpt'] },
];
const TAG_PALETTE = [
  { bg: '#FFF1F2', color: '#DC2626' }, { bg: '#EFF6FF', color: '#2563EB' }, { bg: '#ECFDF5', color: '#059669' },
  { bg: '#FFFBEB', color: '#D97706' }, { bg: '#F5F3FF', color: '#7C3AED' }, { bg: '#FDF2F8', color: '#DB2777' },
  { bg: '#E8F0FE', color: '#0A66C2' }, { bg: '#F0FDFA', color: '#0D9488' },
];

const SOCIAL_PROOF = [
  { value: '50,000+', label: 'Resumes Built' },
  { value: '94%', label: 'Average ATS Score' },
  { value: '120+', label: 'Countries' },
  { value: '4.9★', label: 'User Rating' },
];

const matchesKeywords = (post, kw) => {
  const hay = `${post.category || ''} ${(post.tags || []).join(' ')} ${post.title || ''}`.toLowerCase();
  return kw.some(k => hay.includes(k));
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

/* ═══════════════════════════
   SHARED PIECES
═══════════════════════════ */
const SectionHeading = ({ title, subtitle }) => (
  <div className="bl-sec-head">
    <h2>{title}</h2>
    {subtitle && <p>{subtitle}</p>}
  </div>
);

/* ═══════════════════════════
   HERO
═══════════════════════════ */
const HeroSection = ({ goTo, onBrowse }) => (
  <section className="bl-hero">
    <div className="bl-hero-grid-bg" />
    <div className="bl-hero-glow" />
    <div className="bl-hero-inner">
      <div className="bl-hero-left">
        <div className="bl-hero-badge"><span className="bl-hero-badge-dot" /> Career Resources</div>
        <h1>Career Advice That Helps You Get Hired Faster.</h1>
        <p>Learn resume writing, ATS optimization, interview preparation, LinkedIn strategies, career growth, and AI-powered job search tips from hiring experts.</p>
        <div className="bl-hero-ctas">
          <button type="button" className="bl-btn-primary" style={{ padding: '14px 26px', fontSize: 15 }} onClick={onBrowse}>Browse Articles <ArrowRight size={16} /></button>
          <button type="button" className="bl-btn-ghost-dark" onClick={() => goTo('/build/resume')}>Build Resume Free</button>
        </div>
      </div>
      <div className="bl-hero-right">
        <div className="bl-toolkit-card">
          <div className="bl-toolkit-head">Career Toolkit</div>
          <div className="bl-toolkit-list">
            {TOOLKIT_ITEMS.map(item => (
              <button type="button" key={item.label} className="bl-toolkit-item" onClick={() => goTo(item.path)}>
                <CheckCircle2 size={16} /> {item.label}
              </button>
            ))}
          </div>
          <div className="bl-toolkit-foot">Everything you need to land your next job.</div>
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════
   FEATURED GUIDES
═══════════════════════════ */
const FeaturedSection = ({ posts, navigate }) => {
  if (!posts.length) return null;
  const [large, ...smalls] = posts;
  return (
    <section className="bl-section">
      <SectionHeading title="Featured Guides" />
      <div className="bl-featured-layout">
        <button type="button" className="bl-featured-large" onClick={() => navigate(`/blog/${large.slug}`)}>
          <div className="bl-featured-large-img" style={{ backgroundImage: large.coverImage ? `url(${large.coverImage})` : 'linear-gradient(135deg,#F8FAFC,#EFF6FF)' }}>
            {!large.coverImage && <FileText size={32} />}
          </div>
          <div className="bl-featured-large-body">
            <div className="bl-featured-meta">
              <span className="bl-badge-cat">{large.category}</span>
              <span className="bl-meta-time"><Clock size={12} /> {large.readTime} min read</span>
            </div>
            <h3>{large.title}</h3>
            <p>{large.excerpt}</p>
            <div className="bl-featured-large-foot">
              <div className="bl-author-mini"><span className="bl-avatar-sm">{(large.author || 'M')[0]}</span>{large.author}</div>
              <span className="bl-read-link">Read Guide <ArrowRight size={14} /></span>
            </div>
          </div>
        </button>
        {smalls.length > 0 && (
          <div className="bl-featured-small-col">
            {smalls.map(p => (
              <button type="button" key={p._id} className="bl-featured-small" onClick={() => navigate(`/blog/${p.slug}`)}>
                <div className="bl-featured-small-img" style={{ backgroundImage: p.coverImage ? `url(${p.coverImage})` : 'linear-gradient(135deg,#F8FAFC,#EFF6FF)' }}>
                  {!p.coverImage && <FileText size={20} />}
                </div>
                <div className="bl-featured-small-body">
                  <h4>{p.title}</h4>
                  <div className="bl-featured-small-foot">
                    <span>{p.readTime} min read</span>
                    <span className="bl-read-link-sm">Read <ArrowRight size={11} /></span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

/* ═══════════════════════════
   SEARCH + ARTICLE GRID
═══════════════════════════ */
const ArticleCard = ({ post, navigate }) => (
  <button type="button" className="bl-article-card" onClick={() => navigate(`/blog/${post.slug}`)}>
    <div className="bl-article-img-wrap">
      <div className="bl-article-img" style={{ backgroundImage: post.coverImage ? `url(${post.coverImage})` : 'linear-gradient(135deg,#F8FAFC,#EFF6FF)' }}>
        {!post.coverImage && <FileText size={26} />}
      </div>
      <span className="bl-article-cat-badge">{post.category}</span>
      <span className="bl-article-time-badge"><Clock size={11} /> {post.readTime} min</span>
    </div>
    <div className="bl-article-body">
      <h3 className="bl-article-title">{post.title}</h3>
      <p className="bl-article-desc">{post.excerpt}</p>
      <div className="bl-article-foot">
        <div className="bl-article-author"><span className="bl-avatar-xs">{(post.author || 'M')[0]}</span>{post.author}</div>
        <span className="bl-article-date">{formatDate(post.publishedAt)}</span>
      </div>
      <div className="bl-article-foot2">
        <span className="bl-article-views"><Eye size={12} /> {post.views || 0}</span>
        <span className="bl-read-link-sm">Read Article <ArrowRight size={12} /></span>
      </div>
    </div>
  </button>
);

const SearchGridSection = ({ gridRef, searchQuery, setSearchQuery, activeFilter, setActiveFilter, filteredPosts, navigate }) => (
  <section className="bl-section" ref={gridRef}>
    <SectionHeading title="Explore All Articles" subtitle="Search or filter to find exactly what you need." />
    <div className="bl-search-box">
      <Search size={16} />
      <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search resume, ATS, interview, LinkedIn..." />
      {searchQuery && <button type="button" onClick={() => setSearchQuery('')}><X size={14} /></button>}
    </div>
    <div className="bl-filter-row">
      {SEARCH_FILTERS.map(f => (
        <button type="button" key={f.label} className={`bl-filter-pill${activeFilter?.label === f.label ? ' active' : ''}`}
          onClick={() => setActiveFilter(activeFilter?.label === f.label ? null : f)}>{f.label}</button>
      ))}
    </div>

    {filteredPosts.length === 0 ? (
      <div className="bl-empty-state">
        <div className="bl-empty-icon">📝</div>
        <div className="bl-empty-title">No articles match your search</div>
        <div className="bl-empty-sub">Try a different keyword or clear the filter.</div>
      </div>
    ) : (
      <div className="bl-article-grid">
        {filteredPosts.map(post => <ArticleCard key={post._id} post={post} navigate={navigate} />)}
      </div>
    )}
  </section>
);

/* ═══════════════════════════
   POPULAR TOPICS
═══════════════════════════ */
const PopularTopicsSection = ({ posts, onTopic }) => (
  <section className="bl-section bl-section-tint">
    <SectionHeading title="Popular Topics" subtitle="Browse advice by the topic that matters most to you right now." />
    <div className="bl-topics-grid">
      {POPULAR_TOPICS.map(t => {
        const count = posts.filter(p => matchesKeywords(p, t.kw)).length;
        return (
          <button type="button" key={t.label} className="bl-topic-card" onClick={() => onTopic(t)}>
            <div className="bl-topic-icon" style={{ background: t.bg, color: t.color }}><t.Icon size={20} /></div>
            <div className="bl-topic-title">{t.label}</div>
            <div className="bl-topic-count">{count} article{count !== 1 ? 's' : ''}</div>
            <span className="bl-topic-link">Explore <ArrowRight size={12} /></span>
          </button>
        );
      })}
    </div>
  </section>
);

/* ═══════════════════════════
   FREE TOOLS
═══════════════════════════ */
const FreeToolsSection = ({ goTo }) => (
  <section className="bl-section">
    <SectionHeading title="Free Career Tools" subtitle="Put what you learn into action with MarsResume's AI-powered tools." />
    <div className="bl-tools-grid">
      {FREE_TOOLS.map(tool => (
        <button type="button" key={tool.title} className="bl-tool-card" onClick={() => goTo(tool.path)}>
          <div className="bl-tool-icon" style={{ background: tool.bg, color: tool.color }}><tool.Icon size={20} /></div>
          <div className="bl-tool-title">{tool.title}</div>
          <p className="bl-tool-desc">{tool.desc}</p>
          <span className="bl-tool-link">Try Free <ArrowRight size={12} /></span>
        </button>
      ))}
    </div>
  </section>
);

/* ═══════════════════════════
   TRENDING ARTICLES
═══════════════════════════ */
const TrendingSection = ({ posts, navigate }) => {
  if (!posts.length) return null;
  return (
    <section className="bl-section bl-section-tint">
      <SectionHeading title="Most Read This Week" subtitle="What career-minded readers are opening right now." />
      <div className="bl-trending-scroll">
        {posts.map((p, i) => (
          <button type="button" key={p._id} className="bl-trending-card" onClick={() => navigate(`/blog/${p.slug}`)}>
            <div className="bl-trending-rank">{String(i + 1).padStart(2, '0')}</div>
            <div className="bl-trending-body">
              <h4>{p.title}</h4>
              <div className="bl-trending-meta">
                <span><Eye size={11} /> {p.views || 0} view{p.views === 1 ? '' : 's'}</span>
                <span><Clock size={11} /> {p.readTime} min</span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
};

/* ═══════════════════════════
   LEARNING PATH
═══════════════════════════ */
const LearningPathSection = ({ goTo }) => (
  <section className="bl-section">
    <SectionHeading title="Start Here If You're Job Hunting" subtitle="Follow this path from a blank page to a confident application." />
    <div className="bl-path">
      {LEARNING_PATH.map((step, i) => (
        <React.Fragment key={step.title}>
          <button type="button" className="bl-path-step" onClick={() => goTo(step.path)}>
            <div className="bl-path-num">{step.n}</div>
            <div className="bl-path-icon"><step.Icon size={18} /></div>
            <div className="bl-path-text">
              <div className="bl-path-title">{step.title}</div>
              <p>{step.desc}</p>
            </div>
            <ChevronRight size={16} className="bl-path-arrow-icon" />
          </button>
          {i < LEARNING_PATH.length - 1 && <div className="bl-path-connector" />}
        </React.Fragment>
      ))}
    </div>
  </section>
);

/* ═══════════════════════════
   NEWSLETTER
═══════════════════════════ */
const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribed(true);
  };

  return (
    <section className="bl-section">
      <div className="bl-newsletter-card">
        <div className="bl-newsletter-icon"><Mail size={22} /></div>
        <h2>Get Weekly Career Advice</h2>
        <p>Join thousands of professionals receiving resume tips, ATS updates, interview strategies, and hiring insights every week.</p>
        {subscribed ? (
          <div className="bl-newsletter-success"><CheckCircle2 size={18} /> You're subscribed — check your inbox.</div>
        ) : (
          <form className="bl-newsletter-form" onSubmit={handleSubmit}>
            <input type="email" required placeholder="you@email.com" value={email} onChange={e => setEmail(e.target.value)} />
            <button type="submit" className="bl-btn-primary" style={{ padding: '13px 24px', fontSize: 14 }}>Subscribe</button>
          </form>
        )}
        <div className="bl-newsletter-note">No spam. Unsubscribe anytime.</div>
      </div>
    </section>
  );
};

/* ═══════════════════════════
   AUTHORS
═══════════════════════════ */
const AuthorsSection = ({ authors }) => {
  if (!authors.length) return null;
  return (
    <section className="bl-section bl-section-tint">
      <SectionHeading title="Meet Our Career Experts" subtitle="The writers behind MarsResume's career advice." />
      <div className="bl-authors-grid">
        {authors.map(a => (
          <div key={a.name} className="bl-author-card">
            <div className="bl-author-avatar">{a.avatar ? <img src={a.avatar} alt={a.name} /> : a.name[0]}</div>
            <div className="bl-author-name">{a.name}</div>
            <div className="bl-author-role">Career Content Writer</div>
            <div className="bl-author-count">{a.count} article{a.count !== 1 ? 's' : ''} written</div>
            <a href="https://www.linkedin.com/company/123194112/admin/dashboard/" target="_blank" rel="noopener noreferrer" className="bl-author-linkedin"><Linkedin size={14} /></a>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ═══════════════════════════
   TAGS
═══════════════════════════ */
const TagsSection = ({ onTag }) => (
  <section className="bl-section">
    <SectionHeading title="Browse by Tag" />
    <div className="bl-tags-row">
      {ARTICLE_TAGS.map((t, i) => {
        const palette = TAG_PALETTE[i % TAG_PALETTE.length];
        return (
          <button type="button" key={t.label} className="bl-tag-pill" style={{ background: palette.bg, color: palette.color }} onClick={() => onTag(t)}>
            {t.label}
          </button>
        );
      })}
    </div>
  </section>
);

/* ═══════════════════════════
   SOCIAL PROOF
═══════════════════════════ */
const SocialProofSection = () => (
  <section className="bl-proof-strip">
    <div className="bl-proof-inner">
      <div className="bl-proof-label">Trusted by professionals worldwide</div>
      <div className="bl-proof-stats">
        {SOCIAL_PROOF.map(s => (
          <div key={s.label} className="bl-proof-stat">
            <div className="bl-proof-value">{s.value}</div>
            <div className="bl-proof-label-sm">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ═══════════════════════════
   FINAL CTA
═══════════════════════════ */
const FinalCTASection = ({ goTo, navigate }) => (
  <section className="bl-final-cta-outer">
    <div className="bl-final-cta-card">
      <div className="bl-final-cta-mesh" />
      <div className="bl-final-cta-glow" />
      <div className="bl-final-cta-content">
        <h2>Ready to Improve Your Resume?</h2>
        <p>Build, optimize, tailor, and practice interviews — all from one AI-powered platform.</p>
        <div className="bl-final-cta-actions">
          <button type="button" className="bl-btn-primary" style={{ padding: '14px 26px', fontSize: 15 }} onClick={() => goTo('/build/resume')}>Create Resume <ArrowRight size={16} /></button>
          <button type="button" className="bl-btn-ghost-dark" onClick={() => navigate('/ats-checker')}>Run ATS Check</button>
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════
   POPULAR GUIDES STRIP (above footer)
═══════════════════════════ */
const GuidesStrip = ({ navigate, goTo, onLatest }) => (
  <div className="bl-guides-strip">
    <div className="bl-guides-inner">
      <span className="bl-guides-label">Popular Guides</span>
      <div className="bl-guides-links">
        <button type="button" onClick={() => navigate('/templates#resumes')}>Resume Examples</button>
        <button type="button" onClick={() => navigate('/ats-guide')}>ATS Guide</button>
        <button type="button" onClick={() => navigate('/ai-video-interview')}>Interview Guide</button>
        <button type="button" onClick={() => navigate('/blog')}>Career Blog</button>
        <button type="button" onClick={onLatest}>Latest Articles</button>
      </div>
    </div>
  </div>
);

/* ═══════════════════════════
   BLOG LIST PAGE
═══════════════════════════ */
export const BlogPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState(null);
  const gridRef = useRef(null);

  const blogSchema = useMemo(
    () => (allPosts.length ? buildBlogIndexSchema(allPosts) : null),
    [allPosts],
  );

  usePageSEO({
    title: 'Career Blog – MarsResume | Resume Tips & Career Advice',
    description: 'Expert resume tips, career advice, and job search strategies from the MarsResume team. Level up your job search today.',
    canonical: '/blog',
    jsonLd: blogSchema,
  });

  const goTo = (path) => navigate(user ? (path === '/signup' ? '/dashboard' : path) : path);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/blog?limit=100`)
      .then(r => r.json())
      .then(d => setAllPosts(d.posts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const scrollToGrid = () => gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const applyTopicFilter = (t) => {
    setSearchQuery('');
    setActiveFilter(t);
    scrollToGrid();
  };

  const filteredPosts = useMemo(() => {
    let list = allPosts;
    if (activeFilter) list = list.filter(p => matchesKeywords(p, activeFilter.kw));
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(p => `${p.title} ${p.excerpt} ${p.category} ${(p.tags || []).join(' ')}`.toLowerCase().includes(q));
    }
    return list;
  }, [allPosts, activeFilter, searchQuery]);

  const featuredPosts = useMemo(() => {
    const feats = allPosts.filter(p => p.featured);
    const pool = feats.length >= 3 ? feats : [...feats, ...allPosts.filter(p => !p.featured)];
    return pool.slice(0, 3);
  }, [allPosts]);

  const trendingPosts = useMemo(() => (
    [...allPosts].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 8)
  ), [allPosts]);

  const authors = useMemo(() => {
    const map = new Map();
    allPosts.forEach(p => {
      if (!p.author) return;
      const cur = map.get(p.author) || { name: p.author, avatar: p.authorAvatar, count: 0 };
      cur.count += 1;
      if (!cur.avatar && p.authorAvatar) cur.avatar = p.authorAvatar;
      map.set(p.author, cur);
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count).slice(0, 4);
  }, [allPosts]);

  return (
    <div className="bl-page">
      <BlogStyles />
      <MarketingHeader />

      <HeroSection goTo={goTo} onBrowse={scrollToGrid} />

      {loading ? (
        <div className="bl-loading-wrap"><RocketLoader size={160} message="Loading articles…" /></div>
      ) : (
        <>
          <FeaturedSection posts={featuredPosts} navigate={navigate} />
          <SearchGridSection
            gridRef={gridRef}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            activeFilter={activeFilter}
            setActiveFilter={setActiveFilter}
            filteredPosts={filteredPosts}
            navigate={navigate}
          />
          <PopularTopicsSection posts={allPosts} onTopic={applyTopicFilter} />
          <FreeToolsSection goTo={goTo} />
          <TrendingSection posts={trendingPosts} navigate={navigate} />
          <LearningPathSection goTo={goTo} />
          <NewsletterSection />
          <AuthorsSection authors={authors} />
          <TagsSection onTag={applyTopicFilter} />
        </>
      )}

      <SocialProofSection />
      <FinalCTASection goTo={goTo} navigate={navigate} />
      <GuidesStrip navigate={navigate} goTo={goTo} onLatest={scrollToGrid} />
      <MarketingFooter navigate={navigate} goTo={goTo} />
    </div>
  );
};

/* ═══════════════════════════
   STYLES
═══════════════════════════ */
const BlogStyles = () => (
  <style>{`
    .bl-page { min-height: 100vh; background: #F9FAFB; font-family: 'Inter', sans-serif; color: #111827; overflow-x: hidden; }

    /* Buttons (kept for BlogPostPage compatibility) */
    .bl-btn-primary { background:#EF1D28;color:white;border:none;border-radius:10px;padding:9px 20px;font-weight:700;font-size:13px;cursor:pointer;font-family:inherit;display:inline-flex;align-items:center;gap:7px;transition:all 0.2s;box-shadow:0 2px 8px rgba(239,29,40,0.2); }
    .bl-btn-primary:hover { background:#D91620;transform:translateY(-1px);box-shadow:0 8px 20px rgba(239,29,40,0.32); }
    .bl-btn-ghost-dark { background: rgba(255,255,255,0.06); border: 1.5px solid rgba(255,255,255,0.2); color: white; border-radius: 12px; padding: 14px 26px; font-weight: 700; font-size: 15px; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 8px; transition: all 0.2s; }
    .bl-btn-ghost-dark:hover { background: rgba(255,255,255,0.12); border-color: rgba(255,255,255,0.35); transform: translateY(-2px); }

    /* Blog content (BlogPostPage) */
    .bl-blog-content h1,.bl-blog-content h2,.bl-blog-content h3 { color:#111827;font-weight:800;margin:28px 0 12px;line-height:1.3; }
    .bl-blog-content h1 { font-size:28px; }
    .bl-blog-content h2 { font-size:22px; }
    .bl-blog-content h3 { font-size:18px; }
    .bl-blog-content p { margin:0 0 18px;line-height:1.85;color:#374151; }
    .bl-blog-content ul,.bl-blog-content ol { padding-left:24px;margin:0 0 18px; }
    .bl-blog-content li { margin-bottom:8px;line-height:1.7;color:#374151; }
    .bl-blog-content a { color:#EF1D28;text-decoration:none; }
    .bl-blog-content a:hover { text-decoration:underline; }
    .bl-blog-content blockquote { border-left:4px solid #EF1D28;margin:24px 0;padding:16px 20px;background:#FFF8F8;border-radius:0 12px 12px 0;color:#374151;font-style:italic;font-size:15px; }
    .bl-blog-content code { background:#F3F4F6;border-radius:5px;padding:2px 6px;font-size:13px;font-family:monospace; }
    .bl-blog-content pre { background:#1E293B;color:#E2E8F0;border-radius:14px;padding:22px;overflow-x:auto;margin:20px 0; }
    .bl-blog-content strong { color:#111827;font-weight:700; }
    .bl-blog-content img { max-width:100%;border-radius:14px;margin:16px 0; }

    /* Section shell */
    .bl-section { padding: 76px 48px; max-width: 1180px; margin: 0 auto; }
    .bl-section-tint { background: #FAFBFF; max-width: none; padding-left: 48px; padding-right: 48px; }
    .bl-section-tint > * { max-width: 1180px; margin-left: auto; margin-right: auto; }
    .bl-sec-head { text-align: center; margin-bottom: 48px; }
    .bl-sec-head h2 { margin: 0; font-size: 32px; font-weight: 900; color: #0F172A; letter-spacing: -0.8px; }
    .bl-sec-head p { margin: 12px auto 0; color: #64748B; font-size: 15px; line-height: 1.65; max-width: 580px; }
    .bl-loading-wrap { display: flex; justify-content: center; padding: 100px 24px; }

    /* Badges / pills */
    .bl-badge-cat { background:#F3F4F6; color:#374151; font-size:11px; font-weight:700; padding:4px 12px; border-radius:20px; text-transform: uppercase; letter-spacing: 0.4px; }
    .bl-avatar-sm { width: 26px; height: 26px; border-radius: 50%; background: linear-gradient(135deg,#EF1D28,#FB923C); color: white; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 800; flex-shrink: 0; }
    .bl-avatar-xs { width: 20px; height: 20px; border-radius: 50%; background: linear-gradient(135deg,#EF1D28,#FB923C); color: white; display: inline-flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 800; flex-shrink: 0; }
    .bl-read-link { display: inline-flex; align-items: center; gap: 5px; color: #EF1D28; font-weight: 700; font-size: 13px; }
    .bl-read-link-sm { display: inline-flex; align-items: center; gap: 4px; color: #EF1D28; font-weight: 700; font-size: 12px; }

    /* Hero */
    .bl-hero { background: linear-gradient(160deg,#070E1A 0%,#0D1120 50%,#0A1420 100%); padding: 88px 48px 96px; position: relative; overflow: hidden; }
    .bl-hero-grid-bg { position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,0.06) 1px,transparent 1px); background-size: 28px 28px; pointer-events: none; }
    .bl-hero-glow { position: absolute; top: 10%; left: 15%; width: 700px; height: 420px; background: radial-gradient(ellipse, rgba(239,29,40,0.14) 0%, transparent 70%); pointer-events: none; }
    .bl-hero-inner { max-width: 1180px; margin: 0 auto; display: flex; align-items: center; gap: 56px; position: relative; }
    .bl-hero-left { flex: 0 0 50%; }
    .bl-hero-badge { display: inline-flex; align-items: center; gap: 7px; background: rgba(239,29,40,0.14); border: 1px solid rgba(239,29,40,0.25); border-radius: 24px; padding: 6px 16px; font-size: 11px; font-weight: 700; color: #FCA5A5; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 22px; }
    .bl-hero-badge-dot { width: 6px; height: 6px; border-radius: 50%; background: #EF1D28; }
    .bl-hero-left h1 { margin: 0 0 20px; font-size: 46px; line-height: 1.12; font-weight: 900; letter-spacing: -1.4px; color: white; }
    .bl-hero-left p { margin: 0 0 32px; font-size: 16.5px; line-height: 1.7; color: #94A3B8; max-width: 480px; }
    .bl-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; }
    .bl-hero-right { flex: 1; display: flex; align-items: center; justify-content: center; position: relative; }

    .bl-toolkit-card { width: 340px; background: rgba(255,255,255,0.97); backdrop-filter: blur(18px); border-radius: 20px; box-shadow: 0 40px 90px rgba(0,0,0,0.45); border: 1px solid rgba(255,255,255,0.1); overflow: hidden; }
    .bl-toolkit-head { padding: 18px 22px 12px; font-size: 13px; font-weight: 800; color: #94A3B8; text-transform: uppercase; letter-spacing: 0.6px; border-bottom: 1px solid #F1F5F9; }
    .bl-toolkit-list { padding: 10px 10px; display: flex; flex-direction: column; gap: 2px; }
    .bl-toolkit-item { display: flex; align-items: center; gap: 10px; background: none; border: none; text-align: left; font-family: inherit; padding: 10px 12px; border-radius: 10px; font-size: 13.5px; font-weight: 700; color: #111827; cursor: pointer; transition: background 0.15s; }
    .bl-toolkit-item:hover { background: #FFF1F2; }
    .bl-toolkit-item svg { color: #059669; flex-shrink: 0; }
    .bl-toolkit-foot { padding: 14px 22px 18px; font-size: 12px; color: #9CA3AF; border-top: 1px solid #F1F5F9; margin-top: 4px; }

    /* Featured guides */
    .bl-featured-layout { display: grid; grid-template-columns: 1.4fr 1fr; gap: 28px; align-items: stretch; }
    .bl-featured-large { text-align: left; background: white; border: 1px solid #E5E7EB; border-radius: 22px; overflow: hidden; cursor: pointer; font-family: inherit; padding: 0; transition: box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease; display: flex; flex-direction: column; }
    .bl-featured-large:hover { box-shadow: 0 20px 56px rgba(0,0,0,0.11); transform: translateY(-3px); border-color: #D1D5DB; }
    .bl-featured-large-img { height: 260px; background-size: cover; background-position: center; display: flex; align-items: center; justify-content: center; color: #94A3B8; }
    .bl-featured-large-body { padding: 26px 28px 28px; flex: 1; display: flex; flex-direction: column; }
    .bl-featured-meta { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
    .bl-meta-time { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; color: #9CA3AF; font-weight: 600; }
    .bl-featured-large-body h3 { margin: 0 0 10px; font-size: 22px; font-weight: 800; color: #111827; line-height: 1.35; }
    .bl-featured-large-body p { margin: 0 0 20px; font-size: 14px; color: #6B7280; line-height: 1.7; flex: 1; }
    .bl-featured-large-foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding-top: 18px; border-top: 1px solid #F3F4F6; }
    .bl-author-mini { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; color: #374151; }

    .bl-featured-small-col { display: flex; flex-direction: column; gap: 20px; }
    .bl-featured-small { flex: 1; text-align: left; display: flex; gap: 14px; background: white; border: 1px solid #E5E7EB; border-radius: 18px; padding: 14px; cursor: pointer; font-family: inherit; transition: box-shadow 0.24s ease, transform 0.24s ease, border-color 0.24s ease; align-items: center; }
    .bl-featured-small:hover { box-shadow: 0 18px 44px rgba(0,0,0,0.1); transform: translateY(-3px); border-color: #D1D5DB; }
    .bl-featured-small-img { width: 84px; height: 84px; border-radius: 14px; flex-shrink: 0; background-size: cover; background-position: center; display: flex; align-items: center; justify-content: center; color: #94A3B8; }
    .bl-featured-small-body { min-width: 0; }
    .bl-featured-small-body h4 { margin: 0 0 8px; font-size: 14.5px; font-weight: 800; color: #111827; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
    .bl-featured-small-foot { display: flex; align-items: center; justify-content: space-between; gap: 10px; font-size: 11.5px; color: #9CA3AF; font-weight: 600; }

    /* Search + filters */
    .bl-search-box { max-width: 620px; margin: 0 auto 20px; display: flex; align-items: center; gap: 10px; background: white; border: 1.5px solid #E5E7EB; border-radius: 14px; padding: 12px 18px; transition: border-color 0.15s; }
    .bl-search-box:focus-within { border-color: #EF1D28; box-shadow: 0 0 0 4px rgba(239,29,40,0.08); }
    .bl-search-box svg:first-child { color: #9CA3AF; flex-shrink: 0; }
    .bl-search-box input { flex: 1; border: none; outline: none; font-family: inherit; font-size: 14px; color: #111827; background: transparent; }
    .bl-search-box input::placeholder { color: #9CA3AF; }
    .bl-search-box button { background: none; border: none; cursor: pointer; color: #9CA3AF; display: flex; padding: 2px; }
    .bl-filter-row { display: flex; justify-content: center; flex-wrap: wrap; gap: 8px; margin-bottom: 44px; }
    .bl-filter-pill { border: 1.5px solid #E5E7EB; background: white; color: #374151; border-radius: 20px; padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; transition: all 0.15s; }
    .bl-filter-pill:hover { border-color: #EF1D28; color: #EF1D28; background: #FFF1F2; }
    .bl-filter-pill.active { background: #EF1D28; border-color: #EF1D28; color: white; box-shadow: 0 2px 8px rgba(239,29,40,0.25); }

    /* Article grid */
    .bl-article-grid { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 26px; align-items: stretch; }
    .bl-article-card { display: flex; flex-direction: column; height: 100%; text-align: left; background: white; border: 1px solid #E5E7EB; border-radius: 20px; overflow: hidden; cursor: pointer; font-family: inherit; padding: 0; transition: box-shadow 0.24s ease, transform 0.24s ease, border-color 0.24s ease; }
    .bl-article-card:hover { box-shadow: 0 18px 52px rgba(0,0,0,0.12); transform: translateY(-5px); border-color: #D1D5DB; }
    .bl-article-img-wrap { position: relative; height: 190px; flex-shrink: 0; overflow: hidden; }
    .bl-article-img { width: 100%; height: 100%; background-size: cover; background-position: center; display: flex; align-items: center; justify-content: center; color: #94A3B8; transition: transform 0.4s ease; }
    .bl-article-card:hover .bl-article-img { transform: scale(1.06); }
    .bl-article-cat-badge { position: absolute; top: 12px; left: 12px; background: rgba(17,24,39,0.72); backdrop-filter: blur(6px); color: white; font-size: 10.5px; font-weight: 700; padding: 4px 11px; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.4px; }
    .bl-article-time-badge { position: absolute; top: 12px; right: 12px; background: rgba(255,255,255,0.92); color: #374151; font-size: 10.5px; font-weight: 700; padding: 4px 10px; border-radius: 20px; display: inline-flex; align-items: center; gap: 4px; }
    .bl-article-body { padding: 20px 20px 18px; display: flex; flex-direction: column; flex: 1; }
    .bl-article-title { margin: 0 0 8px; font-size: 15.5px; font-weight: 800; color: #111827; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; min-height: 43px; }
    .bl-article-desc { margin: 0 0 16px; font-size: 13px; color: #6B7280; line-height: 1.6; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; min-height: 42px; flex: 1; }
    .bl-article-foot { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding-top: 14px; border-top: 1px solid #F3F4F6; font-size: 12px; }
    .bl-article-author { display: flex; align-items: center; gap: 7px; color: #374151; font-weight: 600; }
    .bl-article-date { color: #9CA3AF; font-weight: 500; }
    .bl-article-foot2 { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 10px; }
    .bl-article-views { display: inline-flex; align-items: center; gap: 4px; font-size: 11.5px; color: #9CA3AF; font-weight: 600; }

    .bl-empty-state { text-align: center; padding: 60px 0; }
    .bl-empty-icon { font-size: 44px; margin-bottom: 14px; }
    .bl-empty-title { font-size: 17px; font-weight: 700; color: #111827; margin-bottom: 6px; }
    .bl-empty-sub { color: #9CA3AF; font-size: 13.5px; }

    /* Popular topics */
    .bl-topics-grid { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 18px; }
    .bl-topic-card { text-align: left; background: white; border: 1px solid #E5E7EB; border-radius: 18px; padding: 22px; cursor: pointer; font-family: inherit; transition: box-shadow 0.24s ease, transform 0.24s ease, border-color 0.24s ease; }
    .bl-topic-card:hover { box-shadow: 0 16px 40px rgba(15,23,42,0.08); transform: translateY(-4px); border-color: #D1D5DB; }
    .bl-topic-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; margin-bottom: 16px; }
    .bl-topic-title { font-size: 14.5px; font-weight: 800; color: #111827; margin-bottom: 4px; }
    .bl-topic-count { font-size: 12px; color: #9CA3AF; font-weight: 600; margin-bottom: 12px; }
    .bl-topic-link { display: inline-flex; align-items: center; gap: 4px; color: #EF1D28; font-size: 12.5px; font-weight: 700; }

    /* Free tools */
    .bl-tools-grid { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 20px; }
    .bl-tool-card { text-align: left; background: white; border: 1px solid #E5E7EB; border-radius: 18px; padding: 26px; cursor: pointer; font-family: inherit; transition: box-shadow 0.24s ease, transform 0.24s ease, border-color 0.24s ease; }
    .bl-tool-card:hover { box-shadow: 0 16px 40px rgba(15,23,42,0.08); transform: translateY(-4px); border-color: #D1D5DB; }
    .bl-tool-icon { width: 46px; height: 46px; border-radius: 13px; display: flex; align-items: center; justify-content: center; margin-bottom: 18px; }
    .bl-tool-title { font-size: 15.5px; font-weight: 800; color: #111827; margin-bottom: 8px; }
    .bl-tool-desc { margin: 0 0 16px; font-size: 13px; color: #6B7280; line-height: 1.65; }
    .bl-tool-link { display: inline-flex; align-items: center; gap: 5px; color: #EF1D28; font-size: 13px; font-weight: 700; }

    /* Trending */
    .bl-trending-scroll { display: flex; gap: 16px; overflow-x: auto; scrollbar-width: none; -ms-overflow-style: none; margin: 0 -48px; padding: 4px 48px 12px; }
    .bl-trending-scroll::-webkit-scrollbar { display: none; }
    .bl-trending-card { flex: 0 0 auto; width: 280px; text-align: left; display: flex; align-items: center; gap: 16px; background: white; border: 1px solid #E5E7EB; border-radius: 16px; padding: 18px; cursor: pointer; font-family: inherit; transition: box-shadow 0.24s ease, border-color 0.24s ease; }
    .bl-trending-card:hover { box-shadow: 0 14px 36px rgba(15,23,42,0.08); border-color: #D1D5DB; }
    .bl-trending-rank { font-size: 22px; font-weight: 900; color: #FBD3D5; flex-shrink: 0; }
    .bl-trending-body h4 { margin: 0 0 8px; font-size: 13.5px; font-weight: 800; color: #111827; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
    .bl-trending-meta { display: flex; gap: 12px; font-size: 11px; color: #9CA3AF; font-weight: 600; }
    .bl-trending-meta span { display: inline-flex; align-items: center; gap: 4px; }

    /* Learning path */
    .bl-path { display: flex; flex-direction: column; align-items: center; max-width: 580px; margin: 0 auto; }
    .bl-path-step { width: 100%; display: flex; align-items: center; gap: 16px; background: white; border: 1px solid #E5E7EB; border-radius: 16px; padding: 18px 20px; text-align: left; cursor: pointer; font-family: inherit; transition: box-shadow 0.2s ease, border-color 0.2s ease; }
    .bl-path-step:hover { box-shadow: 0 14px 36px rgba(15,23,42,0.08); border-color: #FBD3D5; }
    .bl-path-num { width: 28px; height: 28px; border-radius: 50%; background: #EF1D28; color: white; font-size: 12.5px; font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .bl-path-icon { width: 40px; height: 40px; border-radius: 11px; background: #FFF1F2; color: #EF1D28; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .bl-path-text { flex: 1; min-width: 0; }
    .bl-path-title { font-size: 14.5px; font-weight: 800; color: #111827; margin-bottom: 3px; }
    .bl-path-text p { margin: 0; font-size: 12.5px; color: #6B7280; line-height: 1.5; }
    .bl-path-arrow-icon { color: #D1D5DB; flex-shrink: 0; }
    .bl-path-connector { width: 2px; height: 26px; background: linear-gradient(180deg,#FED7D7,#EF1D28); }

    /* Newsletter */
    .bl-newsletter-card { max-width: 620px; margin: 0 auto; text-align: center; background: linear-gradient(180deg,#FFF8F8 0%,#FFFFFF 100%); border: 1px solid #FBD3D5; border-radius: 24px; padding: 48px 40px; }
    .bl-newsletter-icon { width: 52px; height: 52px; border-radius: 14px; background: #FFF1F2; color: #EF1D28; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; }
    .bl-newsletter-card h2 { margin: 0 0 12px; font-size: 26px; font-weight: 900; color: #0F172A; letter-spacing: -0.6px; }
    .bl-newsletter-card p { margin: 0 0 28px; color: #64748B; font-size: 14.5px; line-height: 1.65; }
    .bl-newsletter-form { display: flex; gap: 10px; max-width: 420px; margin: 0 auto; }
    .bl-newsletter-form input { flex: 1; border: 1.5px solid #E5E7EB; border-radius: 12px; padding: 12px 16px; font-family: inherit; font-size: 14px; outline: none; transition: border-color 0.15s; }
    .bl-newsletter-form input:focus { border-color: #EF1D28; }
    .bl-newsletter-success { display: inline-flex; align-items: center; gap: 8px; color: #059669; font-weight: 700; font-size: 14px; background: #ECFDF5; padding: 12px 20px; border-radius: 12px; }
    .bl-newsletter-note { margin-top: 16px; font-size: 12px; color: #9CA3AF; }

    /* Authors */
    .bl-authors-grid { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 20px; }
    .bl-author-card { background: white; border: 1px solid #E5E7EB; border-radius: 18px; padding: 26px 20px; text-align: center; transition: box-shadow 0.24s ease, border-color 0.24s ease, transform 0.24s ease; }
    .bl-author-card:hover { box-shadow: 0 16px 40px rgba(15,23,42,0.08); transform: translateY(-4px); border-color: #D1D5DB; }
    .bl-author-avatar { width: 60px; height: 60px; border-radius: 50%; background: linear-gradient(135deg,#EF1D28,#FB923C); color: white; font-size: 22px; font-weight: 800; display: flex; align-items: center; justify-content: center; margin: 0 auto 14px; overflow: hidden; }
    .bl-author-avatar img { width: 100%; height: 100%; object-fit: cover; }
    .bl-author-name { font-size: 15px; font-weight: 800; color: #111827; margin-bottom: 3px; }
    .bl-author-role { font-size: 12px; color: #9CA3AF; margin-bottom: 10px; }
    .bl-author-count { font-size: 12px; color: #6B7280; font-weight: 600; margin-bottom: 14px; }
    .bl-author-linkedin { width: 32px; height: 32px; border-radius: 9px; background: #E8F0FE; color: #0A66C2; display: inline-flex; align-items: center; justify-content: center; text-decoration: none; }

    /* Tags */
    .bl-tags-row { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; }
    .bl-tag-pill { border: none; border-radius: 20px; padding: 9px 20px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit; transition: transform 0.15s ease, box-shadow 0.15s ease; }
    .bl-tag-pill:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(15,23,42,0.1); }

    /* Social proof strip */
    .bl-proof-strip { background: white; border-top: 1px solid #F3F4F6; border-bottom: 1px solid #F3F4F6; padding: 36px 48px; }
    .bl-proof-inner { max-width: 1180px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; gap: 32px; flex-wrap: wrap; }
    .bl-proof-label { font-size: 13px; font-weight: 700; color: #6B7280; }
    .bl-proof-stats { display: flex; gap: 40px; flex-wrap: wrap; }
    .bl-proof-stat { text-align: center; }
    .bl-proof-value { font-size: 22px; font-weight: 900; color: #111827; letter-spacing: -0.5px; }
    .bl-proof-label-sm { font-size: 11.5px; color: #9CA3AF; font-weight: 600; margin-top: 2px; }

    /* Final CTA */
    .bl-final-cta-outer { padding: 20px 48px 96px; }
    .bl-final-cta-card { max-width: 1100px; margin: 0 auto; background: linear-gradient(150deg, #060D1A 0%, #0D1120 50%, #0A1428 100%); border-radius: 28px; overflow: hidden; position: relative; padding: 64px 56px; border: 1px solid #1A2A3E; text-align: center; }
    .bl-final-cta-mesh { position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px); background-size: 28px 28px; pointer-events: none; }
    .bl-final-cta-glow { position: absolute; top: -100px; left: 50%; transform: translateX(-50%); width: 600px; height: 400px; background: radial-gradient(ellipse, rgba(239,29,40,0.18) 0%, transparent 65%); pointer-events: none; }
    .bl-final-cta-content { position: relative; max-width: 560px; margin: 0 auto; }
    .bl-final-cta-content h2 { margin: 0 0 14px; font-size: 34px; font-weight: 900; color: white; letter-spacing: -0.8px; line-height: 1.15; }
    .bl-final-cta-content p { margin: 0 0 30px; color: #94A3B8; font-size: 15px; line-height: 1.6; }
    .bl-final-cta-actions { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }

    /* Guides strip */
    .bl-guides-strip { background: #FAFAFA; border-top: 1px solid #F3F4F6; padding: 28px 48px; }
    .bl-guides-inner { max-width: 1140px; margin: 0 auto; display: flex; align-items: center; gap: 20px; flex-wrap: wrap; justify-content: space-between; }
    .bl-guides-label { font-size: 12px; font-weight: 700; color: #111827; text-transform: uppercase; letter-spacing: 0.6px; flex-shrink: 0; }
    .bl-guides-links { display: flex; gap: 10px; flex-wrap: wrap; }
    .bl-guides-links button { background: white; border: 1px solid #E5E7EB; color: #374151; font-size: 12.5px; font-weight: 600; padding: 7px 14px; border-radius: 20px; cursor: pointer; font-family: inherit; transition: all 0.15s; }
    .bl-guides-links button:hover { border-color: #EF1D28; color: #EF1D28; background: #FFF1F2; }

    /* Responsive */
    @media (max-width: 1100px) {
      .bl-topics-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
      .bl-authors-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
    }
    @media (max-width: 1000px) {
      .bl-article-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
      .bl-tools-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
      .bl-featured-layout { grid-template-columns: 1fr; }
    }
    @media (max-width: 900px) {
      .bl-hero { padding: 64px 24px 80px; }
      .bl-hero-inner { flex-direction: column; gap: 44px; text-align: center; }
      .bl-hero-left { flex: none; }
      .bl-hero-left p { margin-left: auto; margin-right: auto; }
      .bl-hero-ctas { justify-content: center; }
      .bl-hero-left h1 { font-size: 36px; }
      .bl-section { padding: 56px 24px; }
      .bl-section-tint { padding-left: 24px; padding-right: 24px; }
      .bl-trending-scroll { margin: 0 -24px; padding: 4px 24px 12px; }
      .bl-final-cta-outer { padding: 12px 24px 72px; }
      .bl-final-cta-card { padding: 48px 28px; }
      .bl-proof-inner { justify-content: center; text-align: center; }
      .bl-guides-inner { flex-direction: column; align-items: flex-start; }
    }
    @media (max-width: 640px) {
      .bl-article-grid { grid-template-columns: 1fr; }
      .bl-tools-grid { grid-template-columns: 1fr; }
      .bl-topics-grid { grid-template-columns: 1fr 1fr; }
      .bl-authors-grid { grid-template-columns: 1fr 1fr; }
      .bl-hero-right { display: none; }
      .bl-hero-left h1 { font-size: 30px; letter-spacing: -1px; }
      .bl-newsletter-form { flex-direction: column; }
      .bl-newsletter-card { padding: 36px 24px; }
      .bl-final-cta-content h2 { font-size: 26px; }
      .bl-proof-stats { gap: 24px; justify-content: center; }
    }
  `}</style>
);

/* ═══════════════════════════
   SINGLE BLOG POST PAGE
═══════════════════════════ */
export const BlogPostPage = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const goTo = (path) => navigate(user ? (path === '/signup' ? '/dashboard' : path) : path);

  const postSchema = useMemo(() => (post ? buildBlogPostSchema(post) : null), [post]);

  usePageSEO({
    title: post ? `${post.title} – MarsResume Blog` : 'Loading… – MarsResume Blog',
    description: post?.excerpt || '',
    canonical: `/blog/${slug}`,
    ogImage: post?.coverImage || undefined,
    ogImageAlt: post?.title || undefined,
    jsonLd: postSchema,
  });

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetch(`${API}/blog/slug/${slug}`)
      .then(r => { if (!r.ok) { setNotFound(true); return null; } return r.json(); })
      .then(d => { if (d) setPost(d); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div style={{ minHeight:'100vh', background:'#F8FAFC', fontFamily:'Inter, sans-serif' }}>
      <BlogStyles />
      <MarketingHeader />
      <div style={{ display:'flex', justifyContent:'center', padding:'120px 24px' }}>
        <RocketLoader size={180} message="Loading post…" />
      </div>
    </div>
  );

  if (notFound || !post) return (
    <div style={{ minHeight:'100vh', background:'#F8FAFC', fontFamily:'Inter, sans-serif' }}>
      <BlogStyles />
      <MarketingHeader />
      <div style={{ textAlign:'center', padding:'120px 24px' }}>
        <div style={{ fontSize:56, marginBottom:16 }}>😕</div>
        <h1 style={{ fontSize:28, fontWeight:900, color:'#111827', marginBottom:8 }}>Post not found</h1>
        <p style={{ color:'#9CA3AF', marginBottom:28 }}>This article may have been moved or deleted.</p>
        <button onClick={() => navigate('/blog')} className="bl-btn-primary" style={{ padding:'12px 28px', fontSize:14 }}>
          <ArrowLeft size={15} /> Back to Blog
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', background:'#F8FAFC', fontFamily:'Inter, sans-serif' }}>
      <BlogStyles />
      <MarketingHeader />

      {/* Post hero */}
      <section style={{ background:'linear-gradient(135deg,#0F172A,#1E293B)', padding:'56px 32px 64px' }}>
        <div style={{ maxWidth:800, margin:'0 auto' }}>
          <button onClick={() => navigate('/blog')} style={{ display:'inline-flex', alignItems:'center', gap:6, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', color:'#94A3B8', borderRadius:8, padding:'6px 14px', fontSize:13, cursor:'pointer', fontFamily:'inherit', marginBottom:28 }}>
            <ArrowLeft size={13} /> Back to Blog
          </button>
          <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
            <span style={{ background:'rgba(239,29,40,0.2)', color:'#FCA5A5', fontSize:11, fontWeight:700, padding:'3px 12px', borderRadius:20 }}>{post.category}</span>
            {post.tags?.map(tag => (
              <span key={tag} style={{ background:'rgba(255,255,255,0.06)', color:'#64748B', fontSize:11, padding:'3px 10px', borderRadius:20 }}>{tag}</span>
            ))}
          </div>
          <h1 style={{ margin:'0 0 16px', fontSize:38, fontWeight:900, color:'white', letterSpacing:-0.8, lineHeight:1.2 }}>{post.title}</h1>
          <p style={{ margin:'0 0 24px', color:'#94A3B8', fontSize:16, lineHeight:1.7 }}>{post.excerpt}</p>
          <div style={{ display:'flex', alignItems:'center', gap:20, flexWrap:'wrap' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:32, height:32, borderRadius:'50%', background:'linear-gradient(135deg,#EF1D28,#FB923C)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800, color:'white' }}>
                {post.author?.[0] || 'M'}
              </div>
              <span style={{ color:'#94A3B8', fontSize:13, fontWeight:600 }}>{post.author}</span>
            </div>
            <span style={{ color:'#334155', fontSize:13, display:'flex', alignItems:'center', gap:4 }}><Calendar size={13} /> {post.publishedAt ? new Date(post.publishedAt).toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'}) : ''}</span>
            <span style={{ color:'#334155', fontSize:13, display:'flex', alignItems:'center', gap:4 }}><Clock size={13} /> {post.readTime} min read</span>
            <span style={{ color:'#334155', fontSize:13, display:'flex', alignItems:'center', gap:4 }}><Eye size={13} /> {post.views} views</span>
          </div>
        </div>
      </section>

      {/* Cover image */}
      {post.coverImage && (
        <div style={{ maxWidth:860, margin:'-32px auto 0', padding:'0 24px' }}>
          <img src={post.coverImage} alt={post.title} style={{ width:'100%', maxHeight:440, objectFit:'cover', borderRadius:16, boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }} />
        </div>
      )}

      {/* Content */}
      <div style={{ maxWidth:760, margin:'0 auto', padding:`${post.coverImage?48:0}px 24px 80px` }}>
        <div className="bl-blog-content" style={{ fontSize:16, lineHeight:1.85, color:'#374151', marginTop: post.coverImage ? 0 : 48 }}
          dangerouslySetInnerHTML={{ __html: post.content }} />

        {/* Author card */}
        <div style={{ marginTop:56, background:'white', border:'1px solid #E5E7EB', borderRadius:16, padding:'28px', display:'flex', alignItems:'flex-start', gap:16 }}>
          <div style={{ width:56, height:56, borderRadius:'50%', background:'linear-gradient(135deg,#EF1D28,#FB923C)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, fontWeight:900, color:'white', flexShrink:0 }}>
            {post.author?.[0] || 'M'}
          </div>
          <div>
            <div style={{ fontSize:11, color:'#9CA3AF', fontWeight:700, textTransform:'uppercase', letterSpacing:0.6, marginBottom:4 }}>Written by</div>
            <div style={{ fontSize:16, fontWeight:800, color:'#111827', marginBottom:4 }}>{post.author}</div>
            <div style={{ fontSize:13, color:'#6B7280' }}>MarsResume Team — helping professionals build careers they love.</div>
          </div>
        </div>

        {/* CTA */}
        <div style={{ marginTop:40, background:'linear-gradient(135deg,#0F172A,#1E293B)', borderRadius:20, padding:'36px', textAlign:'center' }}>
          <h3 style={{ margin:'0 0 10px', fontSize:22, fontWeight:900, color:'white' }}>Ready to build your resume?</h3>
          <p style={{ margin:'0 0 24px', color:'#64748B', fontSize:14 }}>Put these tips into action with MarsResume — free, fast, and AI-powered.</p>
          <button onClick={() => goTo('/signup')} className="bl-btn-primary" style={{ padding:'12px 28px', fontSize:14 }}>
            Create My Resume Free <ArrowRight size={15} />
          </button>
        </div>
      </div>

      <MarketingFooter navigate={navigate} goTo={goTo} />
    </div>
  );
};
